import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { CfiFinding, CfiRating } from '~/data/hooks/useCfi';
import { BUTTON_SX, CARD_SX } from '~/theme';

import { CfiPrecedentBanner } from './CfiPrecedentBanner';
import { ratingColors, ratingLabels } from './ratingLabels';

const RATING_OPTIONS: CfiRating[] = ['favorable', 'neutral', 'unfavorable', 'not_applicable'];

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

type Props = {
  finding: CfiFinding;
  canEdit: boolean;
  onPatch: (
    finding: CfiFinding,
    body: Partial<{
      consultantRating: CfiRating | null;
      consultantSummary: string;
      consultantRecommendedAction: string;
      agreedWithAgent: boolean | null;
      excludedFromReport: boolean;
    }>,
  ) => Promise<void>;
  /** See `cfiLayout`. On the full-page contract view the summary and the recommended action sit side by side from `lg` up. */
  wide?: boolean;
};

export const CfiFindingRow = ({ finding, canEdit, onPatch, wide = false }: Props) => {
  // Baselines == what we render initially. We compare drafts against these to
  // decide whether to PATCH on blur. The editable text is the SHORT summary
  // (what the report shows); the full agentRationale is reference-only under
  // "Show AI original". Falling back to the agent summary (then the rationale)
  // lets the consultant edit pre-populated text instead of an empty box.
  // agentRating is null for consultant-custom findings, but those always carry a
  // consultantRating, so this resolves to a real rating. `?? 'neutral'` is an
  // unreachable belt-and-braces default to keep the type non-null.
  const baselineRating: CfiRating = finding.consultantRating ?? finding.agentRating ?? 'neutral';
  const baselineSummary =
    finding.consultantSummary || finding.agentSummary || finding.agentRationale || '';
  const baselineAction =
    finding.consultantRecommendedAction || finding.agentRecommendedAction || '';

  const [draftRating, setDraftRating] = useState<CfiRating>(baselineRating);
  const [draftSummary, setDraftSummary] = useState<string>(baselineSummary);
  const [draftAction, setDraftAction] = useState<string>(baselineAction);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [showAgentOriginal, setShowAgentOriginal] = useState(false);

  // Sync drafts when the server-side baseline changes (e.g. after a PATCH
  // refetches the finding). Using the documented "Storing information from
  // previous renders" pattern instead of useEffect avoids the unnecessary
  // double-render that the lint rule warns about.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const baselineKey = `${baselineRating}|${baselineSummary}|${baselineAction}`;
  const [prevBaselineKey, setPrevBaselineKey] = useState(baselineKey);
  if (prevBaselineKey !== baselineKey) {
    setPrevBaselineKey(baselineKey);
    setDraftRating(baselineRating);
    setDraftSummary(baselineSummary);
    setDraftAction(baselineAction);
  }

  const flushIfDirty = async () => {
    const body: Parameters<typeof onPatch>[1] = {};
    if (draftRating !== baselineRating) {
      body.consultantRating = draftRating;
    }
    if (draftSummary !== baselineSummary) {
      body.consultantSummary = draftSummary;
    }
    if (draftAction !== baselineAction) {
      body.consultantRecommendedAction = draftAction;
    }
    if (Object.keys(body).length === 0) return;

    setSaveState('saving');
    try {
      await onPatch(finding, body);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (err) {
      console.error('CFI finding save failed', err);
      setSaveState('error');
    }
  };

  const handleDrop = async () => {
    setSaveState('saving');
    try {
      await onPatch(finding, { excludedFromReport: true });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (err) {
      console.error('CFI drop failed', err);
      setSaveState('error');
    }
  };

  const handleUndrop = async () => {
    setSaveState('saving');
    try {
      await onPatch(finding, { excludedFromReport: false });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (err) {
      console.error('CFI undrop failed', err);
      setSaveState('error');
    }
  };

  const heading = finding.provisionLabel || finding.provisionId;

  // A finding is a panel sitting directly under the summary Card, which the theme
  // rounds to 16px. Left at MUI's 4px the two read as different design systems
  // stacked on each other.
  const rowSurfaceSx = CARD_SX;

  if (finding.excludedFromReport) {
    return (
      <Box
        sx={{
          ...rowSurfaceSx,
          p: 1.5,
          mb: 1,
          opacity: 0.6,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            <strong>{heading}</strong> — dropped from report
          </Typography>
          {canEdit ? (
            <Button size="small" onClick={handleUndrop} sx={BUTTON_SX}>
              Undo
            </Button>
          ) : null}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      onBlur={(e) => {
        if (!canEdit) return;
        // Only flush when focus leaves the entire row.
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        void flushIfDirty();
      }}
      sx={{
        ...rowSurfaceSx,
        p: 2,
        mb: 1.5,
      }}
    >
      {finding.agentPrecedentDisagreement ? (
        <CfiPrecedentBanner explanation={finding.agentPrecedentDisagreementExplanation} />
      ) : null}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {heading}
          </Typography>
          {finding.source === 'ai_discovered' ? (
            <Chip
              size="small"
              icon={<AutoAwesomeIcon />}
              label="AI-discovered"
              color="info"
              variant="outlined"
            />
          ) : finding.source === 'consultant_custom' ? (
            <Chip
              size="small"
              icon={<PersonIcon />}
              label={
                finding.consultantAddedByName
                  ? `Custom · ${finding.consultantAddedByName}`
                  : 'Custom'
              }
              color="secondary"
              variant="outlined"
            />
          ) : (
            <Chip
              size="small"
              icon={<GavelIcon />}
              label="Core provision"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {saveState === 'saving' ? (
            <Typography variant="caption" color="text.secondary">
              Saving…
            </Typography>
          ) : null}
          {saveState === 'saved' ? (
            <Typography variant="caption" color="success.main">
              Saved ✓
            </Typography>
          ) : null}
          {saveState === 'error' ? (
            <Typography variant="caption" color="error.main">
              Save failed
            </Typography>
          ) : null}
          {canEdit ? (
            <Tooltip title="Drop from report">
              <IconButton
                size="small"
                color="warning"
                onClick={handleDrop}
                aria-label="Drop from report"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ mb: 2 }}>
        {canEdit ? (
          <TextField
            select
            size="small"
            label="Rating"
            value={draftRating}
            onChange={(e) => setDraftRating(e.target.value as CfiRating)}
            sx={{ width: { xs: '100%', sm: 240 } }}
          >
            {RATING_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                <Chip
                  size="small"
                  color={ratingColors[option]}
                  label={ratingLabels[option]}
                  sx={{ pointerEvents: 'none' }}
                />
              </MenuItem>
            ))}
          </TextField>
        ) : (
          // Read-only state: render the chip directly so its rating color is
          // preserved. A disabled MUI select would mute the chip's filled
          // palette (white text on color → black on gray), which loses the
          // at-a-glance favorability cue in the approved report.
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Rating
            </Typography>
            <Chip
              size="small"
              color={ratingColors[draftRating]}
              label={ratingLabels[draftRating]}
            />
          </Box>
        )}

        <Box
          display="grid"
          gridTemplateColumns={wide ? { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } : '1fr'}
          gap={2}
          alignItems="start"
        >
          {canEdit ? (
            <TextField
              label="Summary (shown in the report)"
              value={draftSummary}
              multiline
              minRows={2}
              maxRows={10}
              fullWidth
              size="small"
              onChange={(e) => setDraftSummary(e.target.value)}
              helperText={
                !finding.consultantSummary
                  ? 'Prepopulated from the agent — edit to override. The full rationale is under "Show AI original".'
                  : undefined
              }
            />
          ) : (
            // Read-only: render as plain text so the body keeps its normal
            // contrast (a disabled multiline TextField washes the summary out
            // to muted gray, which is hard to read in the approved report).
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Summary
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {draftSummary || '—'}
              </Typography>
            </Box>
          )}

          {/* Recommended action — the report's action-list card. Shown for every
            finding so the consultant can always add one, but only below-market
            findings carry an agent-proposed action to prepopulate; for the rest
            it starts empty (and only below-market actions reach the report's
            action list). Always rendered so a typed value can never be saved
            while hidden. */}
          {canEdit ? (
            <TextField
              label="Recommended action (shown in the report's action list)"
              value={draftAction}
              multiline
              minRows={2}
              maxRows={8}
              fullWidth
              size="small"
              onChange={(e) => setDraftAction(e.target.value)}
              helperText={
                !finding.consultantRecommendedAction && finding.agentRecommendedAction
                  ? 'Prepopulated from the agent — edit to override.'
                  : undefined
              }
            />
          ) : draftAction ? (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Recommended action
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {draftAction}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Stack>

      {/* Consultant-custom findings have no agent score, so there's no "AI
          original & evidence" to reveal — hide the toggle entirely for them. */}
      {canEdit && finding.agentRating != null ? (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowAgentOriginal((v) => !v)}
            sx={BUTTON_SX}
            endIcon={
              <Chip
                size="small"
                label={`${Math.round(Number(finding.agentConfidence) * 100)}%`}
                color={ratingColors[finding.agentRating]}
                sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            }
          >
            {showAgentOriginal ? 'Hide' : 'Show'} AI original &amp; evidence
          </Button>
        </Stack>
      ) : null}

      {finding.agentRating != null ? (
        <Collapse in={showAgentOriginal} unmountOnExit>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Agent original —{' '}
              <Box
                component="span"
                sx={{
                  color:
                    ratingColors[finding.agentRating] === 'default'
                      ? 'text.primary'
                      : `${ratingColors[finding.agentRating]}.main`,
                }}
              >
                {ratingLabels[finding.agentRating]}
              </Box>{' '}
              · confidence {Math.round(Number(finding.agentConfidence) * 100)}%
            </Typography>

            {finding.agentSummary ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
                  {finding.agentSummary}
                </Typography>
              </>
            ) : null}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Full rationale
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
              {finding.agentRationale}
            </Typography>

            {finding.agentRecommendedAction ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Recommended action
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
                  {finding.agentRecommendedAction}
                </Typography>
              </>
            ) : null}

            {finding.agentQualifyingLanguage.length > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Qualifying language
                </Typography>
                <Stack spacing={1}>
                  {finding.agentQualifyingLanguage.map((quote, idx) => (
                    <Box
                      key={`${quote.chunkId}-${idx}`}
                      sx={{ borderLeft: 3, borderColor: 'info.main', pl: 1.5 }}
                    >
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        &ldquo;{quote.quote.replace(/\s+/g, ' ').trim()}&rdquo;
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            ) : null}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
};
